/* =========================================================
   SIMPLY AMAZING APPLES — EVENTS DRAWER
   Calendar drawer + event detail drawer interactions
========================================================= */

(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-saa-events-section]';

  /*
   * Must match the CSS:
   * html.saa-events-scroll-locked,
   * body.saa-events-scroll-locked
   */
  const BODY_LOCK_CLASS = 'saa-events-scroll-locked';

  /*
   * Drawers and overlays now use the same class.
   * This matches the CSS selectors ending in .is-open.
   */
  const OPEN_CLASS = 'is-open';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const instances = new Map();

  let activeInstance = null;
  let lastTrigger = null;

  class SAAEventsDrawer {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId || section.id || '';

      this.calendarDrawer = section.querySelector(
        '[data-saa-calendar-drawer]'
      );

      this.calendarOverlay = section.querySelector(
        '[data-saa-calendar-overlay]'
      );

      this.eventOverlay = section.querySelector(
        '[data-saa-event-overlay]'
      );

      this.eventDrawers = Array.from(
        section.querySelectorAll('[data-saa-event-drawer]')
      );

      this.activeEventDrawer = null;
      this.boundClick = this.onClick.bind(this);

      if (
        !this.calendarDrawer ||
        !this.calendarOverlay ||
        !this.eventOverlay
      ) {
        return;
      }

      this.section.addEventListener('click', this.boundClick);

      this.resetState();
    }

    resetState() {
      this.calendarDrawer.classList.remove(OPEN_CLASS);
      this.calendarOverlay.classList.remove(OPEN_CLASS);
      this.eventOverlay.classList.remove(OPEN_CLASS);

      this.calendarDrawer.setAttribute('aria-hidden', 'true');
      this.calendarOverlay.setAttribute('aria-hidden', 'true');
      this.eventOverlay.setAttribute('aria-hidden', 'true');

      /*
       * Always return drawers to their true top position.
       */
      this.calendarDrawer.scrollTop = 0;

      this.eventDrawers.forEach((drawer) => {
        drawer.classList.remove(OPEN_CLASS);
        drawer.setAttribute('aria-hidden', 'true');
        drawer.scrollTop = 0;
      });

      this.activeEventDrawer = null;
    }

    onClick(event) {
      const closeCalendarButton = event.target.closest(
        '[data-saa-close-calendar]'
      );

      const eventButton = event.target.closest(
        '[data-saa-calendar-event]'
      );

      const closeEventButton = event.target.closest(
        '[data-saa-close-event]'
      );

      if (
        closeCalendarButton ||
        event.target === this.calendarOverlay
      ) {
        event.preventDefault();
        this.closeAll(true);
        return;
      }

      if (eventButton) {
        event.preventDefault();

        const key = eventButton.dataset.saaCalendarEvent;

        this.openEvent(key, eventButton);
        return;
      }

      if (
        closeEventButton ||
        event.target === this.eventOverlay
      ) {
        event.preventDefault();
        this.closeEvent(true);
      }
    }

    openCalendar(trigger = null) {
      closeOtherInstances(this);

      activeInstance = this;
      lastTrigger = trigger || document.activeElement;

      this.closeEvent(false);

      /*
       * Reset before showing it.
       */
      this.calendarDrawer.scrollTop = 0;

      this.calendarDrawer.classList.add(OPEN_CLASS);
      this.calendarOverlay.classList.add(OPEN_CLASS);

      this.calendarDrawer.setAttribute('aria-hidden', 'false');
      this.calendarOverlay.setAttribute('aria-hidden', 'false');

      lockBody();

      /*
       * Reset again after the browser paints the open drawer.
       */
      window.requestAnimationFrame(() => {
        this.calendarDrawer.scrollTop = 0;

        window.requestAnimationFrame(() => {
          this.calendarDrawer.scrollTop = 0;
          this.focusFirst(this.calendarDrawer);
        });
      });
    }

    closeCalendar(restoreFocus = true) {
      this.calendarDrawer.classList.remove(OPEN_CLASS);
      this.calendarOverlay.classList.remove(OPEN_CLASS);

      this.calendarDrawer.setAttribute('aria-hidden', 'true');
      this.calendarOverlay.setAttribute('aria-hidden', 'true');

      this.calendarDrawer.scrollTop = 0;

      if (!this.activeEventDrawer) {
        unlockBody();

        if (activeInstance === this) {
          activeInstance = null;
        }

        if (restoreFocus) {
          restorePreviousFocus();
        }
      }
    }

    openEvent(key, trigger = null) {
      if (!key) return;

      const drawer = this.eventDrawers.find((item) => {
        return item.dataset.saaEventDrawer === key;
      });

      if (!drawer) {
        console.warn(
          `[SAA Events] No event drawer found for key: ${key}`
        );

        return;
      }

      lastTrigger = trigger || document.activeElement;
      activeInstance = this;

      /*
       * Reset all event drawers before selecting the target.
       */
      this.eventDrawers.forEach((item) => {
        item.classList.remove(OPEN_CLASS);
        item.setAttribute('aria-hidden', 'true');
        item.scrollTop = 0;
      });

      /*
       * Reset the selected drawer before opening it.
       */
      drawer.scrollTop = 0;

      drawer.classList.add(OPEN_CLASS);
      drawer.setAttribute('aria-hidden', 'false');

      this.activeEventDrawer = drawer;

      this.eventOverlay.classList.add(OPEN_CLASS);
      this.eventOverlay.setAttribute('aria-hidden', 'false');

      /*
       * Hide the calendar underneath the event detail drawer.
       */
      this.calendarDrawer.classList.remove(OPEN_CLASS);
      this.calendarOverlay.classList.remove(OPEN_CLASS);

      this.calendarDrawer.setAttribute('aria-hidden', 'true');
      this.calendarOverlay.setAttribute('aria-hidden', 'true');

      lockBody();

      /*
       * Force the event detail drawer to begin at its actual top.
       * This prevents it from reopening halfway down.
       */
      window.requestAnimationFrame(() => {
        drawer.scrollTop = 0;

        window.requestAnimationFrame(() => {
          drawer.scrollTop = 0;
          this.focusFirst(drawer);
        });
      });
    }

    closeEvent(restoreFocus = true) {
      if (this.activeEventDrawer) {
        this.activeEventDrawer.classList.remove(OPEN_CLASS);
        this.activeEventDrawer.setAttribute(
          'aria-hidden',
          'true'
        );

        this.activeEventDrawer.scrollTop = 0;
      }

      this.eventDrawers.forEach((drawer) => {
        drawer.classList.remove(OPEN_CLASS);
        drawer.setAttribute('aria-hidden', 'true');
        drawer.scrollTop = 0;
      });

      this.activeEventDrawer = null;

      this.eventOverlay.classList.remove(OPEN_CLASS);
      this.eventOverlay.setAttribute('aria-hidden', 'true');

      unlockBody();

      if (activeInstance === this) {
        activeInstance = null;
      }

      if (restoreFocus) {
        restorePreviousFocus();
      }
    }

     closeAll(restoreFocus = true) {
      this.activeEventDrawer = null;

      this.eventDrawers.forEach((drawer) => {
        drawer.classList.remove(OPEN_CLASS);
        drawer.setAttribute('aria-hidden', 'true');
        drawer.scrollTop = 0;
      });

      this.eventOverlay.classList.remove(OPEN_CLASS);
      this.eventOverlay.setAttribute('aria-hidden', 'true');

      this.calendarDrawer.classList.remove(OPEN_CLASS);
      this.calendarOverlay.classList.remove(OPEN_CLASS);

      this.calendarDrawer.setAttribute('aria-hidden', 'true');
      this.calendarOverlay.setAttribute('aria-hidden', 'true');

      this.calendarDrawer.scrollTop = 0;

      unlockBody();

      if (activeInstance === this) {
        activeInstance = null;
      }

      if (restoreFocus) {
        restorePreviousFocus();
      }
    }

    focusFirst(container) {
      const preferredClose = container.querySelector(
        '[data-saa-close-calendar], [data-saa-close-event]'
      );

      const firstFocusable =
        preferredClose ||
        container.querySelector(FOCUSABLE_SELECTOR);

      if (!firstFocusable) return;

      /*
       * preventScroll keeps focusing the close button from
       * moving the drawer away from scrollTop 0.
       */
      firstFocusable.focus({
        preventScroll: true
      });
    }

    trapFocus(event) {
      const container =
        this.activeEventDrawer ||
        (
          this.calendarDrawer.classList.contains(OPEN_CLASS)
            ? this.calendarDrawer
            : null
        );

      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((element) => {
        return element.offsetParent !== null;
      });

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.section.removeEventListener(
        'click',
        this.boundClick
      );

      this.closeAll(false);
    }
  }

  function lockBody() {
    document.documentElement.classList.add(
      BODY_LOCK_CLASS
    );

    document.body.classList.add(
      BODY_LOCK_CLASS
    );
  }

  function unlockBody() {
    const anyOpen = Array.from(
      instances.values()
    ).some((instance) => {
      return (
        instance.calendarDrawer?.classList.contains(
          OPEN_CLASS
        ) ||
        Boolean(instance.activeEventDrawer)
      );
    });

    if (!anyOpen) {
      document.documentElement.classList.remove(
        BODY_LOCK_CLASS
      );

      document.body.classList.remove(
        BODY_LOCK_CLASS
      );
    }
  }

  function restorePreviousFocus() {
    const target = lastTrigger;

    lastTrigger = null;

    if (
      target instanceof HTMLElement &&
      document.contains(target)
    ) {
      window.requestAnimationFrame(() => {
        target.focus({
          preventScroll: true
        });
      });
    }
  }

  function closeOtherInstances(current) {
    instances.forEach((instance) => {
      if (instance !== current) {
        instance.closeAll(false);
      }
    });
  }

  function findInstanceForTrigger(trigger) {
    const target = trigger.dataset.saaEventsTarget;

    if (target) {
      const cleanTarget = target.replace(/^#/, '');

      const matched = Array.from(
        instances.values()
      ).find((instance) => {
        return (
          instance.section.id === cleanTarget ||
          instance.sectionId === cleanTarget
        );
      });

      if (matched) {
        return matched;
      }
    }

    return instances.values().next().value || null;
  }

  function initializeSection(section) {
    if (
      !(section instanceof HTMLElement) ||
      instances.has(section)
    ) {
      return;
    }

    const instance = new SAAEventsDrawer(section);

    if (!instance.calendarDrawer) {
      return;
    }

    instances.set(section, instance);
  }

  function initializeAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initializeSection);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(
      '[data-saa-open-calendar]'
    );

    if (!trigger) return;

    const instance = findInstanceForTrigger(trigger);

    if (!instance) return;

    event.preventDefault();

    instance.openCalendar(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (!activeInstance) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      activeInstance.closeAll(true);
      return;
    }

    if (event.key === 'Tab') {
      activeInstance.trapFocus(event);
    }
  });

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target.matches?.(
        SECTION_SELECTOR
      )
        ? event.target
        : event.target.querySelector?.(
            SECTION_SELECTOR
          );

      if (!section) return;

      const instance = instances.get(section);

      instance?.destroy();

      instances.delete(section);
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      const section = event.target.matches?.(
        SECTION_SELECTOR
      )
        ? event.target
        : event.target.querySelector?.(
            SECTION_SELECTOR
          );

      if (!section) return;

      instances.get(section)?.openCalendar();
    }
  );

  document.addEventListener(
    'shopify:section:deselect',
    (event) => {
      const section = event.target.matches?.(
        SECTION_SELECTOR
      )
        ? event.target
        : event.target.querySelector?.(
            SECTION_SELECTOR
          );

      if (!section) return;

      instances.get(section)?.closeAll(false);
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll()
    );
  } else {
    initializeAll();
  }
})();